import { useState, useEffect } from "react";
import {
  Plus,
  Edit2,
  Trash2,
  Users,
  Phone,
  MapPin,
  CreditCard,
  Briefcase,
  ExternalLink
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { API } from "@/lib/api";

const VENDOR_SERVICE_TYPES = [
  "Laundry",
  "Tailoring",
  "Repair & Maintenance",
  "Misc. Services",
  "Commerce",
];

const initialVendorForm = {
  merchant_name: "",
  service_type: "",
  custom_service: "",
  contact_no: "",
  upi_id: "",
  address: "",
  maps_link: ""
};

export const VendorMaster = () => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [formData, setFormData] = useState(initialVendorForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchVendors();
  }, []);

  const fetchVendors = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/vendors`);
      const data = await response.json();
      setVendors(data);
    } catch (error) {
      console.error("Error fetching vendors:", error);
      toast.error("Failed to load vendors");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const openCreateDialog = () => {
    setSelectedVendor(null);
    setFormData(initialVendorForm);
    setDialogOpen(true);
  };

  const openEditDialog = (vendor) => {
    setSelectedVendor(vendor);
    const isKnownType = VENDOR_SERVICE_TYPES.includes(vendor.service_type);
    setFormData({
      merchant_name: vendor.merchant_name,
      service_type: isKnownType ? vendor.service_type : "Misc. Services",
      custom_service: isKnownType ? "" : vendor.service_type,
      contact_no: vendor.contact_no,
      upi_id: vendor.upi_id,
      address: vendor.address,
      maps_link: vendor.maps_link || ""
    });
    setDialogOpen(true);
  };

  const openDeleteDialog = (vendor) => {
    setSelectedVendor(vendor);
    setDeleteDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.merchant_name.trim()) {
      toast.error("Merchant name is required");
      return;
    }
    if (!formData.service_type) {
      toast.error("Service type is required");
      return;
    }
    if (formData.service_type === "Misc. Services" && !formData.custom_service.trim()) {
      toast.error("Please specify the service description");
      return;
    }
    if (!formData.contact_no.trim()) {
      toast.error("Contact number is required");
      return;
    }

    const resolvedServiceType = formData.service_type === "Misc. Services"
      ? formData.custom_service.trim()
      : formData.service_type;

    const { custom_service, ...rest } = formData;
    const payload = { ...rest, service_type: resolvedServiceType };

    setSaving(true);
    try {
      const url = selectedVendor 
        ? `${API}/vendors/${selectedVendor.id}`
        : `${API}/vendors`;
      
      const response = await fetch(url, {
        method: selectedVendor ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error("Failed to save vendor");
      }

      toast.success(selectedVendor ? "Vendor updated!" : "Vendor created!");
      setDialogOpen(false);
      fetchVendors();
    } catch (error) {
      console.error("Error saving vendor:", error);
      toast.error("Failed to save vendor");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedVendor) return;

    try {
      const response = await fetch(`${API}/vendors/${selectedVendor.id}`, {
        method: "DELETE"
      });

      if (!response.ok) {
        throw new Error("Failed to delete vendor");
      }

      toast.success("Vendor deleted!");
      setDeleteDialogOpen(false);
      setSelectedVendor(null);
      fetchVendors();
    } catch (error) {
      console.error("Error deleting vendor:", error);
      toast.error("Failed to delete vendor");
    }
  };

  return (
    <div className="space-y-6" data-testid="vendor-master-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Vendor Master</h1>
          <p className="text-slate-500 mt-1">Manage your vendors and merchants</p>
        </div>
        
        <Button 
          onClick={openCreateDialog}
          className="bg-[#0047FF] hover:bg-[#0033CC] btn-interactive"
          data-testid="add-vendor-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Vendor
        </Button>
      </div>

      {/* Vendor Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-pulse text-slate-500">Loading vendors...</div>
        </div>
      ) : vendors.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {vendors.map((vendor) => (
            <Card 
              key={vendor.id} 
              className="border-slate-200 hover:shadow-lg transition-shadow"
              data-testid={`vendor-card-${vendor.id}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg font-semibold text-slate-900">
                      {vendor.merchant_name}
                    </CardTitle>
                    <p className="text-sm text-[#0047FF] font-medium mt-1">
                      {vendor.service_type}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openEditDialog(vendor)}
                      className="h-8 w-8 text-slate-400 hover:text-slate-600"
                      data-testid={`edit-vendor-${vendor.id}`}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openDeleteDialog(vendor)}
                      className="h-8 w-8 text-slate-400 hover:text-red-500"
                      data-testid={`delete-vendor-${vendor.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Phone className="w-4 h-4 text-slate-400" />
                  {vendor.contact_no}
                </div>
                {vendor.upi_id && (
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <CreditCard className="w-4 h-4 text-slate-400" />
                    {vendor.upi_id}
                  </div>
                )}
                {vendor.address && (
                  <div className="flex items-start gap-2 text-sm text-slate-600">
                    <MapPin className="w-4 h-4 text-slate-400 mt-0.5" />
                    <span className="line-clamp-2">{vendor.address}</span>
                  </div>
                )}
                {vendor.maps_link && (
                  <a href={vendor.maps_link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800">
                    <ExternalLink className="w-4 h-4" />
                    <span>Open in Maps</span>
                  </a>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border-slate-200">
          <CardContent className="py-16 text-center">
            <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-700 mb-2">No vendors yet</h3>
            <p className="text-slate-500 mb-6">Add your first vendor to get started</p>
            <Button 
              onClick={openCreateDialog}
              className="bg-[#0047FF] hover:bg-[#0033CC]"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Vendor
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {selectedVendor ? "Edit Vendor" : "Add New Vendor"}
            </DialogTitle>
            <DialogDescription>
              {selectedVendor 
                ? "Update the vendor details below."
                : "Enter the vendor details to add them to your master list."
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="merchant_name">Merchant Name *</Label>
              <Input
                id="merchant_name"
                value={formData.merchant_name}
                onChange={(e) => handleInputChange("merchant_name", e.target.value)}
                placeholder="e.g., Vannurappa Ironing Shop"
                data-testid="input-merchant-name"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="service_type">Type of Service *</Label>
              <Select
                value={formData.service_type}
                onValueChange={(value) => {
                  setFormData(prev => ({
                    ...prev,
                    service_type: value,
                    custom_service: value === "Misc. Services" ? prev.custom_service : ""
                  }));
                }}
              >
                <SelectTrigger id="service_type" data-testid="input-service-type">
                  <SelectValue placeholder="Select service type" />
                </SelectTrigger>
                <SelectContent>
                  {VENDOR_SERVICE_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formData.service_type === "Misc. Services" && (
                <Input
                  value={formData.custom_service}
                  onChange={(e) => handleInputChange("custom_service", e.target.value)}
                  placeholder="Specify service description"
                  data-testid="input-custom-service"
                />
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="contact_no">Contact Number *</Label>
              <Input
                id="contact_no"
                value={formData.contact_no}
                onChange={(e) => handleInputChange("contact_no", e.target.value)}
                placeholder="e.g., 9876543210"
                data-testid="input-contact-no"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="upi_id">Payment Detail (UPI ID)</Label>
              <Input
                id="upi_id"
                value={formData.upi_id}
                onChange={(e) => handleInputChange("upi_id", e.target.value)}
                placeholder="e.g., vendor@upi"
                data-testid="input-upi-id"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => handleInputChange("address", e.target.value)}
                placeholder="Full address"
                data-testid="input-address"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="maps_link">Google Maps Link</Label>
              <Input
                id="maps_link"
                value={formData.maps_link}
                onChange={(e) => handleInputChange("maps_link", e.target.value)}
                placeholder="https://maps.google.com/..."
                data-testid="input-maps-link"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSave}
              disabled={saving}
              className="bg-[#0047FF] hover:bg-[#0033CC]"
              data-testid="save-vendor-btn"
            >
              {saving ? "Saving..." : (selectedVendor ? "Update" : "Add Vendor")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Vendor</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedVendor?.merchant_name}"? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
